import React, { useState }  from 'react';
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import DraggableContainer from '../shared/DraggableContainer.js';
import FeedbackWidget from '../shared/FeedbackWidget.js';
import { submitFeedback } from '../../services/feedbackService.js';

/**
 * Timeline Component
 * Displays a vertical timeline of events with interactive elements and modal functionality
 * @param {Object[]} events - Array of event objects to display in timeline
 * @param {string} storyId - ID of the current story
 */
const Timeline = ({events, storyId}) => {

  // Add formatDate helper function
  const formatDate = (dateString) => {
    try {
      const [datePart, timePart] = dateString.split('T');
      const [day, month, year] = datePart.split('-');
      const time = timePart.split('+')[0]; // Extract time without timezone
      return `${day}/${month}/${year} ${time}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // ==================================================
  // State Management
  // ==================================================
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState('');

  // ==================================================
  // Event Handlers
  // ==================================================
  const openModal = (event) => {
    const formattedContent = event.formatted_event.replace(/\\n/g, '\n');
    setModalContent(formattedContent);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const handleFeedbackSubmit = async (feedbackData) => {
    try {
      await submitFeedback(feedbackData);
      // Optionally add success message or state update
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      // Optionally handle error (show message, etc.)
    }
  };

  if (!events || events.length === 0) return null;


  // ==================================================
  // Main Render
  // ==================================================
  return (
    <>
      {/* Timeline Container */}
      <VerticalTimeline
        animate={false}
        layout='1-column-left'
        lineColor='lightblue'
      >
        {/* Timeline Events */}
        {[...events].map((event, index) => (
          <VerticalTimelineElement
            key={event.event_ref || index}
            className="vertical-timeline-element"
            iconStyle={{ 
              display: 'flex',
              background: '#fff', // Set background to white
              border: event.importance === "Importance élevée" 
                ? '6px solid orange' 
                : event.importance === "Importance moyenne" 
                ? '6px solid green' 
                : '6px solid grey',
              color: 'black',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
            icon={
              <span 
                className="material-symbols-outlined"
              >
                {event.type === "Message" ? "mail" :
                 event.type === "Compte Rendu" ? "description" :
                 "call_log"}
              </span>
            }
            iconOnClick={() => openModal(event)}
          >
            {/* Event Header Information */}
            <h4 className="text-sm font-bold text-blue-500">
              {event.type}
            </h4>
            <h4 className="text-sm font-bold text-gray-500">
              {formatDate(event.date)}
            </h4>
            <h3 className="text-lg font-bold">{event.event_summary}</h3>
            <p className="text-gray-600">Détails: {event.detailed_info}</p>
            {/*<p className="text-blue-300">{event.recommendation}</p>*/}
            
            {/* Contact Information Section */}
            {event.contact && event.contact.length > 0 && (
              <div className="mt-2">
                <h4 className="font-semibold">Contacts:</h4>
                <ul>
                  {event.contact.map((contact, idx) => (
                    <li key={idx} className="text-sm">
                      {contact.name} {contact.role && `- ${contact.role}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tracking Actions Section */}
            {event.tracking_actions && event.tracking_actions.length > 0 && (
              <div className="mt-2">
                <h4 className="font-semibold">Actions:</h4>
                  {event.tracking_actions.map((action, idx) => (
                    <li key={idx} className="text-sm">
                      {/* Convert objects to string */}
                      {typeof action.action === "object" 
                        ? JSON.stringify(action.action) 
                        : action.action}{" "}
                      {/* -{" "}
                      {typeof action.status === "object" 
                        ? JSON.stringify(action.status) 
                        : action.status} */}
                    </li>
                  ))}
              </div>
            )}
            <FeedbackWidget
              contentId={event.event_ref || `event-${index}`}
              storyId={storyId}
              contentType="timeline"
              contentSection={event.type || "event"}
              onSubmitFeedback={handleFeedbackSubmit}
              hasBeenSubmitted={false} // You might want to track this in state
            />
          </VerticalTimelineElement>
        ))}
      </VerticalTimeline>

      {/* Modal for Detailed Event View */}
      {modalIsOpen && (
          <DraggableContainer 
            title="Original Event" 
            initialPosition={{ top: 100, left: 360 }}
            initialWidth="calc((100% - 355px)/2)"
            initialHeight="calc(100% - 150px)"
            style={{ pointerEvents: 'auto' }}
            attachToViewport={true}
            onClose={closeModal}
          >
            <pre style={{
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              fontFamily: 'inherit',
              padding: '20px'
            }}>
              {modalContent}
            </pre>
          </DraggableContainer>
      )}

    </>
  );
};

export default Timeline;